import { useEffect, useState, type ReactNode } from "react";
import { restoreSession, useSession } from "@/lib/store/session";
import { FullPageLoader } from "@/components/FullPageLoader";

/**
 * Reconcile the session with the cookie before the router decides anything
 * (#289).
 *
 * The auth guard reads store state only, so a returning user whose
 * localStorage Safari had purged would be bounced to the sign-in page a beat
 * before the cookie could restore them. That race only exists when there is no
 * guid, so that is the only case that blocks: with a guid we render
 * immediately and reconcile in the background, where the same call mints the
 * cookie that makes the next purge a non-event. It is idempotent — once a
 * cookie exists the server stops issuing new ones.
 */
export function SessionBoot({ children }: { children: ReactNode }) {
  const hadGuid = useState(() => !!useSession.getState().userId)[0];
  const [ready, setReady] = useState(hadGuid);

  useEffect(() => {
    let cancelled = false;
    restoreSession().finally(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return <FullPageLoader />;
  return <>{children}</>;
}
